import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useNotification } from "./useNotification";

/** Subscribe to the browser's online/offline events. */
function subscribe(callback: () => void): () => void {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
}

function getSnapshot(): boolean {
  return navigator.onLine;
}

function getServerSnapshot(): boolean {
  return true; // SSR always assumes online
}

/**
 * Tracks browser online/offline status and shows notification toasts
 * when connectivity changes.
 *
 * @returns `true` if the browser reports being online.
 */
export function useOnlineStatus(): boolean {
  const isOnline = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  const { showNotification, dismissNotification } = useNotification();
  const offlineNotificationId = useRef<string | null>(null);
  const wasOnline = useRef(true);

  const handleOnline = useCallback(() => {
    // Dismiss the persistent offline notification
    if (offlineNotificationId.current) {
      dismissNotification(offlineNotificationId.current);
      offlineNotificationId.current = null;
    }
    showNotification("You're back online.", "success", 3000);
  }, [showNotification, dismissNotification]);

  const handleOffline = useCallback(() => {
    const id = showNotification(
      "You appear to be offline. Some features may be unavailable.",
      "warning",
      null, // persistent — no auto-dismiss
    );
    offlineNotificationId.current = id;
  }, [showNotification]);

  useEffect(() => {
    // React to changes in isOnline
    if (isOnline && !wasOnline.current) {
      handleOnline();
    } else if (!isOnline && wasOnline.current) {
      handleOffline();
    }
    wasOnline.current = isOnline;
  }, [isOnline, handleOnline, handleOffline]);

  // If page loads while already offline, show notification immediately
  useEffect(() => {
    if (!navigator.onLine) {
      wasOnline.current = false;
      handleOffline();
    }
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isOnline;
}
